import type { EventHandler, EventHandlerMap } from '../types/events';

export class EventTerminal<
  IncomingEvent extends Record<string, string>,
  IncomingEventDetails extends Record<IncomingEvent[keyof IncomingEvent], unknown>,
  OutgoingEvent extends Record<string, string>,
  OutgoingEventDetails extends Record<OutgoingEvent[keyof OutgoingEvent], unknown>,
> {
  protected eventListeners: EventHandlerMap<IncomingEvent, IncomingEventDetails> = {};
  private incomingEventListeners: Partial<Record<IncomingEvent[keyof IncomingEvent], (event: Event) => void>> = {};

  constructor(incomingEvents: IncomingEvent) {
    this.initWindowListeners(incomingEvents);
  }

  private handleEvent(eventName: IncomingEvent[keyof IncomingEvent], rawEvent: Event): void {
    const event = rawEvent as CustomEvent<IncomingEventDetails[typeof eventName]>;
    this.eventListeners[eventName]?.forEach((callback) => callback(event.detail));
  }

  private initWindowListeners(incomingEvents: IncomingEvent): void {
    Object.values(incomingEvents).forEach((rawEventName) => {
      const eventName = rawEventName as IncomingEvent[keyof IncomingEvent];
      const eventHandler = this.handleEvent.bind(this, eventName);
      window.addEventListener(eventName, eventHandler);
      this.incomingEventListeners[eventName] = eventHandler;
    });
  }

  addEventListener<Name extends IncomingEvent[keyof IncomingEvent]>(
    eventName: Name,
    listener: EventHandler<IncomingEventDetails[Name]>
  ): void {
    if (!this.eventListeners[eventName]) this.eventListeners[eventName] = [];
    this.eventListeners[eventName].push(listener);
  }

  removeEventListener<Name extends IncomingEvent[keyof IncomingEvent]>(
    eventName: Name,
    listener: EventHandler<IncomingEventDetails[Name]>
  ): void {
    if (!this.eventListeners[eventName]) return;
    const handlerIndex = this.eventListeners[eventName].indexOf(listener);
    if (handlerIndex === -1) return;
    this.eventListeners[eventName].splice(handlerIndex, 1);
  }

  emit<Name extends OutgoingEvent[keyof OutgoingEvent]>(name: Name, detail?: OutgoingEventDetails[Name]): void {
    window.dispatchEvent(new CustomEvent(name.toString(), { detail: detail }));
  }

  close(): void {
    Object.entries(this.incomingEventListeners).forEach(([eventName, eventHandler]) => {
      window.removeEventListener(eventName, eventHandler as EventListener);
    });
  }
}
